import { AlertTriangle, ArrowLeft, Check, Info, Loader2, Minus, Plus } from 'lucide-react'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../../References/URLs'
import ActiveStoreContext from '../../../store/ActiveStoreContext'
import AuthContext from '../../../store/auth-context'
import styles from './DRSettings.module.css'

/* ── constants ──────────────────────────────────────── */
const DEFAULT_SLOTS = [
    '21:00', '21:15', '21:30', '21:45',
    '22:00', '22:15', '22:30', '22:45',
    '23:00', '23:15', '23:30', '23:45',
]

/** Convert "22:15" → "10:15 PM" for display */
const formatSlot = (slot) => {
    const [hStr, mStr] = slot.split(':')
    const h = parseInt(hStr, 10)
    const suffix = h >= 12 ? 'PM' : 'AM'
    const h12 = h > 12 ? h - 12 : h
    return `${h12}:${mStr} ${suffix}`
}
const QUICK_LIMITS = [1, 2, 3, 5, 10]
const MIN_LIMIT = 1
const MAX_LIMIT = 10

/* ── Skeleton bar ───────────────────────────────────── */
const SkeletonBar = ({ w = '100%', h = 14 }) => (
    <div className={styles.skeletonBar} style={{ width: w, height: h }} />
)

const DRSettings = () => {
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = (() => {
        try { return asCtx?.activeStore && asCtx.activeStore.trim() !== '' ? JSON.parse(asCtx.activeStore) : null }
        catch { return null }
    })()
    const navigate = useNavigate()

    /* ── remote state ── */
    const [loading, setLoading]           = useState(true)
    const [receivers, setReceivers]       = useState([])
    const [loadingRecv, setLoadingRecv]   = useState(true)
    const [availableSlots, setAvailableSlots] = useState(DEFAULT_SLOTS)

    /* ── form state ── */
    const [autoEnabled, setAutoEnabled]   = useState(false)
    const [selectedSlot, setSelectedSlot] = useState('22:00')
    const [autoRetry, setAutoRetry]       = useState(false)
    const [manualLimit, setManualLimit]   = useState(3)
    const [isSaving, setIsSaving]         = useState(false)
    const [infoDismissed, setInfoDismissed] = useState(false)

    /* ── initial values for dirty detection ── */
    const initialRef = useRef(null)

    /* ── API calls ── */
    const getSettings = async () => {
        setLoading(true)
        try {
            const res = await fetch(BASE_URL_API + 'dailyReportsGetSettings', {
                method: 'POST',
                body: JSON.stringify({ token: authCtx.token, storeIdentifier: activeStore?.storeIdentifier }),
            })
            if (!res.ok) return
            const data = await res.json()
            if (data?.status === 'success') {
                const s      = data?.response?.settings
                const wins   = data?.response?.availableWindows
                const isFirst = data?.response?.isFirstTime === true
                if (wins?.length) setAvailableSlots(wins)
                const init = {
                    autoEnabled:  s?.autoReportEnabled === 1,
                    selectedSlot: s?.preferredWindowStart ?? '22:00',
                    autoRetry:    s?.autoRetryOnFailure === 1,
                    manualLimit:  s?.maxManualSendsPerDay ?? 3,
                    isFirstTime:  isFirst,
                }
                setAutoEnabled(init.autoEnabled)
                setSelectedSlot(init.selectedSlot)
                setAutoRetry(init.autoRetry)
                setManualLimit(init.manualLimit)
                initialRef.current = init
            } else if (data?.message === 'tokenExpired') {
                authCtx.logout()
            }
        } catch { /* silent */ } finally { setLoading(false) }
    }

    const getReceivers = async () => {
        setLoadingRecv(true)
        try {
            const res = await fetch(BASE_URL_API + 'dailyReportsGetReceivers', {
                method: 'POST',
                body: JSON.stringify({ token: authCtx.token, storeIdentifier: activeStore?.storeIdentifier }),
            })
            if (!res.ok) return
            const data = await res.json()
            if (data?.status === 'success') setReceivers(data?.response?.receivers ?? [])
            else if (data?.message === 'tokenExpired') authCtx.logout()
        } catch { /* silent */ } finally { setLoadingRecv(false) }
    }

    const saveSettings = async () => {
        if (isSaving) return
        if (autoEnabled && activeReceivers.length === 0) {
            toast.error('Add at least one active receiver before enabling auto-report.')
            return
        }
        setIsSaving(true)
        try {
            const res = await fetch(BASE_URL_API + 'dailyReportsSaveSettings', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    autoReportEnabled:    autoEnabled ? 1 : 0,
                    preferredWindowStart: selectedSlot,
                    maxManualSendsPerDay: manualLimit,
                    autoRetryOnFailure:   autoRetry ? 1 : 0,
                }),
            })
            if (!res.ok) { toast.error('Something went wrong.'); return }
            const data = await res.json()
            if (data?.status === 'success') {
                toast.success('Settings saved.')
                initialRef.current = { autoEnabled, selectedSlot, autoRetry, manualLimit }
                setTimeout(() => navigate('/reports/daily'), 800)
            } else if (data?.message === 'tokenExpired') {
                authCtx.logout()
            } else if (data?.message === 'noActiveReceivers') {
                toast.error('Add at least one active receiver before enabling auto-report.')
            } else if (data?.message === 'invalidWindowSlot') {
                toast.error('Please select a valid delivery window slot.')
            } else {
                toast.error('Failed to save settings.')
            }
        } catch { toast.error('Something went wrong.') } finally { setIsSaving(false) }
    }

    const discard = () => {
        if (!initialRef.current) return
        const i = initialRef.current
        setAutoEnabled(i.autoEnabled)
        setSelectedSlot(i.selectedSlot)
        setAutoRetry(i.autoRetry)
        setManualLimit(i.manualLimit)
    }

    useEffect(() => {
        getSettings()
        getReceivers()
    }, [activeStore?.storeIdentifier])

    /* ── dirty detection ── */
    const isDirty = initialRef.current
        ? initialRef.current.autoEnabled !== autoEnabled
            || initialRef.current.selectedSlot !== selectedSlot
            || initialRef.current.autoRetry !== autoRetry
            || initialRef.current.manualLimit !== manualLimit
        : false

    /* ── back navigation guard ── */
    const handleBack = () => {
        if (isDirty && !window.confirm('You have unsaved changes. Leave without saving?')) return
        navigate('/reports/daily')
    }

    const activeReceivers = receivers.filter(r => r.isActive === 1)
    const showNoReceiverBanner = autoEnabled && !loadingRecv && activeReceivers.length === 0
    const showInfoBanner = !loading && !infoDismissed && initialRef.current
        && (initialRef.current.isFirstTime || (!initialRef.current.autoEnabled && !autoEnabled))

    /* ── stepper helpers ── */
    const stepDown = () => setManualLimit(v => Math.max(MIN_LIMIT, v - 1))
    const stepUp   = () => setManualLimit(v => Math.min(MAX_LIMIT, v + 1))

    return (
        <div className={styles.wrapper}>
            {/* ── Page Header ── */}
            <div className={styles.pageHeader}>
                <button className={styles.backBtn} onClick={handleBack} type="button">
                    <ArrowLeft size={14} /> Daily reports
                </button>
                <h1 className={styles.pageTitle}>Report settings</h1>
            </div>

            {/* ── Settings Card ── */}
            <div className={styles.card}>

                {/* ─ Row 1: Auto-report master toggle ─ */}
                <div className={styles.settingRow}>
                    <div className={styles.settingText}>
                        <div className={styles.settingLabel}>Auto-report</div>
                        <div className={styles.settingDesc}>Automatically send daily reports every night at your preferred time</div>
                    </div>
                    {loading
                        ? <SkeletonBar w={44} h={24} />
                        : <button
                            className={`${styles.toggle} ${autoEnabled ? styles.toggleOn : ''}`}
                            onClick={() => setAutoEnabled(v => !v)}
                            type="button"
                            aria-label="Toggle auto-report"
                        >
                            <span className={styles.toggleThumb} />
                        </button>}
                </div>

                <div className={styles.rowDivider} />

                {/* ─ Row 2: Delivery window ─ */}
                <div className={`${styles.settingSection} ${!autoEnabled ? styles.dimmed : ''}`}>
                    <div className={styles.settingRowInline}>
                        <div className={styles.settingText}>
                            <div className={styles.settingLabel}>Preferred delivery window</div>
                            <div className={styles.settingDesc}>Reports go out in this 15-minute slot each night</div>
                        </div>
                        <div className={styles.selectedSlotBadge}>
                            {loading ? <SkeletonBar w={90} h={20} /> : formatSlot(selectedSlot)}
                        </div>
                    </div>
                    {loading ? (
                        <div className={styles.slotGrid}>
                            {availableSlots.map((_, i) => <SkeletonBar key={i} h={32} />)}
                        </div>
                    ) : (
                        <div className={styles.slotGrid}>
                            {availableSlots.map(slot => (
                                <button
                                    key={slot}
                                    className={`${styles.slotBtn} ${selectedSlot === slot ? styles.slotBtnActive : ''}`}
                                    onClick={() => autoEnabled && setSelectedSlot(slot)}
                                    type="button"
                                    disabled={!autoEnabled}
                                >
                                    {formatSlot(slot)}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className={styles.rowDivider} />

                {/* ─ Row 3: Auto-retry toggle ─ */}
                <div className={`${styles.settingRow} ${!autoEnabled ? styles.dimmed : ''}`}>
                    <div className={styles.settingText}>
                        <div className={styles.settingLabel}>Auto-retry on failure</div>
                        <div className={styles.settingDesc}>If tonight's send fails, retry in the next available window (same night only)</div>
                    </div>
                    {loading
                        ? <SkeletonBar w={44} h={24} />
                        : <button
                            className={`${styles.toggle} ${autoRetry ? styles.toggleOn : ''}`}
                            onClick={() => autoEnabled && setAutoRetry(v => !v)}
                            type="button"
                            aria-label="Toggle auto-retry"
                            disabled={!autoEnabled}
                        >
                            <span className={styles.toggleThumb} />
                        </button>}
                </div>

                <div className={styles.rowDivider} />

                {/* ─ Row 4: Manual send limit ─ */}
                <div className={styles.settingSection}>
                    <div className={styles.settingRowInline}>
                        <div className={styles.settingText}>
                            <div className={styles.settingLabel}>Manual send limit</div>
                            <div className={styles.settingDesc}>How many times managers can send manually per day</div>
                        </div>
                        {loading ? (
                            <SkeletonBar w={100} h={36} />
                        ) : (
                            <div className={styles.stepper}>
                                <button
                                    className={styles.stepperBtn}
                                    onClick={stepDown}
                                    disabled={manualLimit <= MIN_LIMIT}
                                    type="button"
                                    aria-label="Decrease"
                                >
                                    <Minus size={14} />
                                </button>
                                <span className={styles.stepperVal}>{manualLimit}</span>
                                <button
                                    className={styles.stepperBtn}
                                    onClick={stepUp}
                                    disabled={manualLimit >= MAX_LIMIT}
                                    type="button"
                                    aria-label="Increase"
                                >
                                    <Plus size={14} />
                                </button>
                            </div>
                        )}
                    </div>
                    {!loading && (
                        <div className={styles.quickSelect}>
                            {QUICK_LIMITS.map(v => (
                                <button
                                    key={v}
                                    className={`${styles.quickBtn} ${manualLimit === v ? styles.quickBtnActive : ''}`}
                                    onClick={() => setManualLimit(v)}
                                    type="button"
                                >
                                    {v}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* ── Banners ── */}
            {showNoReceiverBanner && (
                <div className={styles.bannerWarn}>
                    <AlertTriangle size={16} className={styles.bannerIcon} />
                    <div className={styles.bannerText}>
                        <strong>Auto-report requires at least one active receiver.</strong>
                        {' '}Add a receiver first.{' '}
                        <Link to="/config/daily-reports" className={styles.bannerLink}>Manage receivers →</Link>
                    </div>
                </div>
            )}
            {showInfoBanner && (
                <div className={styles.bannerInfo}>
                    <Info size={16} className={styles.bannerIcon} />
                    <div className={styles.bannerText}>
                        You haven't set up auto-report yet. Configure your preferred window and enable the toggle above to get started.
                    </div>
                    <button className={styles.bannerDismiss} onClick={() => setInfoDismissed(true)} type="button">
                        <Check size={13} />
                    </button>
                </div>
            )}

            {/* ── Save / Discard ── */}
            <div className={styles.saveRow}>
                <button
                    className={styles.discardBtn}
                    onClick={discard}
                    disabled={isSaving || !isDirty}
                    type="button"
                >
                    Discard changes
                </button>
                <button
                    className={`${styles.saveBtn} ${(showNoReceiverBanner || isSaving) ? styles.saveBtnDisabled : ''}`}
                    onClick={saveSettings}
                    disabled={showNoReceiverBanner || isSaving}
                    type="button"
                >
                    {isSaving
                        ? <><Loader2 size={14} className={styles.spinIcon} /> Saving…</>
                        : <><Check size={14} /> Save settings</>}
                </button>
            </div>
        </div>
    )
}

export default DRSettings
